import { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import AuthShell from "../../components/Auth/AuthShell";
import {
  registerUser,
  resendVerificationEmail,
} from "../../api/services/authService";
import {
  getLocalizedErrorMessage,
  getTranslationKey,
  getValidationErrorMessage,
} from "../../utils/errorMessages";
import ThemeToggle from "../../components/ThemeToggle";
import { useToast } from "../../components/ToastContext";

export default function ProfessionalRegistration() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [emailAlreadyRegistered, setEmailAlreadyRegistered] = useState(false);
  const [emailAlreadyVerified, setEmailAlreadyVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    username: "",
    designation: "",
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    document.documentElement.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (submitError) setSubmitError("");
    if (name === "email") {
      if (emailAlreadyRegistered) setEmailAlreadyRegistered(false);
      if (emailAlreadyVerified) setEmailAlreadyVerified(false);
    }
    if (passwordError && (name === "password" || name === "confirmPassword")) {
      setPasswordError("");
    }
  };

  const isRTL = i18n.language === "ar";

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (isSubmitting) return;

    if (formData.password !== formData.confirmPassword) {
      setPasswordError(
        t("auth.professional_registration.step3.password_error"),
      );
      return;
    }

    setPasswordError("");
    setSubmitError("");
    setEmailAlreadyRegistered(false);
    setEmailAlreadyVerified(false);
    setIsSubmitting(true);

    const payload = {
      email: formData.email,
      first_name: formData.firstName,
      last_name: formData.lastName,
      username: formData.username,
      designation: formData.designation,
      password: formData.password,
      confirm_password: formData.confirmPassword,
      role: "professional",
    };

    try {
      await registerUser(payload);
      setIsSubmitted(true);
    } catch (error) {
      const errorCode = error?.code;
      const translationKey = getTranslationKey(error?.message);
      const normalizedMessage = (error?.message || "").toLowerCase().trim();
      const isEmailExists =
        errorCode === "AUTH008" ||
        errorCode === "USER001" ||
        translationKey === "auth.api_messages.email_already_registered" ||
        normalizedMessage.includes("email already registered") ||
        normalizedMessage.includes("email already exists") ||
        normalizedMessage.includes("email already") ||
        normalizedMessage.includes("already registered");
      const isEmailVerified =
        errorCode === "AUTH016" ||
        translationKey === "auth.api_messages.email_already_verified" ||
        normalizedMessage.includes("already verified");
      // For validation errors (422), build a localized message from the
      // structured payload so users see the real reason in their language.
      const isValidationError = error?.status === 422;
      const validationMessage = isValidationError
        ? getValidationErrorMessage(error, t)
        : null;
      const localizedError =
        validationMessage ||
        getLocalizedErrorMessage(
          error?.message,
          t,
          t("auth.professional_registration.step4.error"),
        );
      const localizedEmailExists = t(
        "auth.api_messages.email_already_registered",
      );
      setEmailAlreadyVerified(isEmailVerified);
      setEmailAlreadyRegistered(
        !isEmailVerified &&
          (isEmailExists || localizedError === localizedEmailExists),
      );
      setSubmitError(localizedError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendEmail = async () => {
    if (!formData.email || isResending) return;
    setIsResending(true);
    try {
      await resendVerificationEmail(formData.email);
      showToast(
        t("auth.professional_registration.email_exists.resend_toast", {
          email: formData.email,
        }),
        "success",
      );
    } catch (error) {
      showToast(
        t("auth.professional_registration.email_exists.resend_error"),
        "error",
      );
    } finally {
      setIsResending(false);
    }
  };

  const isFormComplete =
    formData.email &&
    formData.firstName &&
    formData.lastName &&
    formData.username &&
    formData.designation &&
    formData.password &&
    formData.confirmPassword &&
    formData.password === formData.confirmPassword &&
    agreedToTerms &&
    !emailAlreadyRegistered &&
    !emailAlreadyVerified &&
    !isSubmitting;

  const fieldMargin = "mb-2";
  const professionalHeroSuffix = t(
    "auth.registration.professional_hero.suffix",
  );

  return (
    <div className="min-h-screen relative">
      <div className="fixed bottom-6 right-6 z-40">
        <ThemeToggle />
      </div>

      <AuthShell
        tagline={t("auth.registration.tagline")}
        title={
          <>
            {t("auth.registration.professional_hero.prefix")}
            <br />
            <span className="text-(--color-primary)">
              {t("auth.registration.professional_hero.highlight")}
            </span>
            {professionalHeroSuffix && <> {professionalHeroSuffix}</>}
          </>
        }
        subtitle={t("auth.professional_registration.step1.description")}
        formEyebrow={t("auth.registration.professional_form_eyebrow")}
        formMaxWidthClass="max-w-lg"
        formTitle={
          isSubmitted
            ? t("auth.professional_registration.step5.title")
            : t("auth.professional_registration.step1.title")
        }
      >
        {isSubmitted ? (
          <>
            <div className="mb-6">
              <p className="text-(--color-text-muted) mb-2 text-sm">
                {t("auth.professional_registration.step5.sent_to")}
              </p>
              <p className="text-(--color-primary) font-medium text-sm">
                {formData.email}
              </p>
            </div>

            <p className="text-(--color-text-muted) text-sm mb-6">
              {t("auth.professional_registration.step5.check_inbox")}
              <br />
              {t("auth.professional_registration.step5.expires")}
            </p>

            <div className="space-y-4 w-2/3 mx-auto">
              <Button onClick={() => navigate("/login")}>
                {t("auth.professional_registration.step5.login_button")}
              </Button>

              <button
                onClick={handleResendEmail}
                className="w-full py-3 text-(--color-text-muted) hover:text-(--color-primary) transition-colors text-sm"
              >
                {t("auth.professional_registration.step5.resend_text")}{" "}
                <span className="text-(--color-primary) hover:underline">
                  {t("auth.professional_registration.step5.resend_link")}
                </span>
              </button>
            </div>
          </>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label={t("auth.professional_registration.step1.email_label")}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin={fieldMargin}
                  placeholder={t(
                    "auth.professional_registration.step1.email_placeholder",
                  )}
                  required
                />

                <Input
                  label={t(
                    "auth.professional_registration.step3.username_label",
                  )}
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  margin={fieldMargin}
                  placeholder={t(
                    "auth.professional_registration.step3.username_placeholder",
                  )}
                  required
                />
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  label={t(
                    "auth.professional_registration.step3.first_name_label",
                  )}
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  margin={fieldMargin}
                  placeholder={t(
                    "auth.professional_registration.step3.first_name_placeholder",
                  )}
                  required
                />

                <Input
                  label={t(
                    "auth.professional_registration.step3.last_name_label",
                  )}
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  margin={fieldMargin}
                  placeholder={t(
                    "auth.professional_registration.step3.last_name_placeholder",
                  )}
                  required
                />

                <Input
                  label={t(
                    "auth.professional_registration.step3.designation_label",
                  )}
                  type="text"
                  name="designation"
                  value={formData.designation}
                  onChange={handleChange}
                  margin={fieldMargin}
                  placeholder={t(
                    "auth.professional_registration.step3.designation_placeholder",
                  )}
                  required
                />
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label={t(
                    "auth.professional_registration.step3.password_label",
                  )}
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  margin={fieldMargin}
                  placeholder={t(
                    "auth.professional_registration.step3.password_placeholder",
                  )}
                  required
                />

                <Input
                  label={t(
                    "auth.professional_registration.step3.confirm_password_label",
                  )}
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  margin={fieldMargin}
                  placeholder={t(
                    "auth.professional_registration.step3.confirm_password_placeholder",
                  )}
                  required
                />
              </div>

              {passwordError && (
                <div className="p-2 bg-red-500/10 border border-red-500 rounded-lg text-red-500 text-sm">
                  {passwordError}
                </div>
              )}

              <label
                className={`flex items-start gap-2 p-3 bg-(--color-bg-darker) border border-(--color-border) rounded-lg cursor-pointer hover:border-(--color-primary) transition-colors ${isRTL ? "flex-row-reverse" : ""}`}
              >
                <div className="relative shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                      agreedToTerms
                        ? "bg-(--color-primary) border-(--color-primary)"
                        : "bg-transparent border-(--color-border)"
                    }`}
                  >
                    {agreedToTerms && (
                      <Check className="w-3 h-3 text-(--color-text-on-primary)" />
                    )}
                  </div>
                </div>
                <p
                  className={`text-xs text-(--color-text-muted) ${isRTL ? "text-right" : ""}`}
                >
                  {t("auth.professional_registration.step4.terms_text")}{" "}
                  <a
                    href="#"
                    className="text-(--color-primary) hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("auth.professional_registration.step4.terms_link")}
                  </a>{" "}
                  {t("auth.professional_registration.step4.and_text")}{" "}
                  <a
                    href="#"
                    className="text-(--color-primary) hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {t("auth.professional_registration.step4.privacy_link")}
                  </a>
                </p>
              </label>

              {emailAlreadyVerified ? (
                <div className="rounded-xl border border-(--color-border) bg-(--color-bg-darker) p-4 text-sm">
                  <p className="text-(--color-text-primary) font-semibold mb-1">
                    {t("auth.professional_registration.email_verified.title")}
                  </p>
                  <p className="text-(--color-text-muted) mb-4">
                    {t("auth.professional_registration.email_verified.body")}
                  </p>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => navigate("/login")}
                  >
                    {t(
                      "auth.professional_registration.email_verified.login_button",
                    )}
                  </Button>
                </div>
              ) : emailAlreadyRegistered ? (
                <div className="rounded-xl border border-(--color-border) bg-(--color-bg-darker) p-4 text-sm">
                  <p className="text-(--color-text-primary) font-semibold mb-1">
                    {t("auth.professional_registration.email_exists.title")}
                  </p>
                  <p className="text-(--color-text-muted) mb-4">
                    {t("auth.professional_registration.email_exists.body")}
                  </p>
                  <div className="space-y-3">
                    <Button
                      type="button"
                      onClick={handleResendEmail}
                      disabled={!formData.email || isResending}
                    >
                      {isResending
                        ? t(
                            "auth.professional_registration.email_exists.resending",
                          )
                        : t(
                            "auth.professional_registration.email_exists.resend_button",
                          )}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => navigate("/login")}
                    >
                      {t(
                        "auth.professional_registration.email_exists.login_button",
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                submitError && (
                  <div className="text-red-500 text-sm">{submitError}</div>
                )
              )}

              {!emailAlreadyRegistered && !emailAlreadyVerified && (
                <Button type="submit" disabled={!isFormComplete}>
                  {isSubmitting
                    ? t("auth.professional_registration.step4.submitting")
                    : t("auth.professional_registration.step4.submit_button")}
                </Button>
              )}
            </form>

            <div className="mt-4 text-center text-sm text-(--color-text-muted) dark:text-gray-400">
              <span>
                {t("auth.professional_registration.common.signin_text")}{" "}
              </span>
              <button
                onClick={() => navigate("/login")}
                className="accent-link ml-1 font-medium"
              >
                {t("auth.professional_registration.common.signin_link")}
              </button>
            </div>
          </>
        )}
      </AuthShell>
    </div>
  );
}
